import { Specimen } from '#~/gamepad/components/specimen/index.js';
import { useGamepadIds } from '#~/gamepad/hooks.js';
import { ActionBox, Heading } from '../layout/index.js';
import { ControllersList } from './ip-controller/index.js';
import { Layout } from './layout.jsx';
import type { Props } from './types.js';
import { AnimatePresence, motion, Motions } from '@studio/design-system/motion';
import * as Tabs from '@studio/design-system/tabs';
import { useLatestFn, useShallowEffect } from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { twMerge } from 'cn';
import { getInfo } from '@studio/models/gamepad';
import { ItemsList } from './components/list.js';
import { isEmpty } from '@tryiris-ai/js-types';
import { Callout } from '@studio/design-system/callout';
import { Sel, STagNew } from '#~/stag/index.js';

/** `<Controllers>` panel. */
export const Controllers = ({ className, ...props }: Props): ReactElement => {
  const gamepadIds = useGamepadIds();
  const controllers = gamepadIds.map(getInfo).sort((a, b) => a.name.localeCompare(b.name));
  const list = controllers.map((controller) => ({
    id: controller.gamepadId,
    left: controller.name
  }));

  const [selection, setSelection] = useState<T_Maybe<(typeof list)[number]>>(() => list?.[0]);

  const handleSync = useLatestFn(() => {
    if (
      (selection && !gamepadIds.includes(selection.id)) ||
      (!selection && gamepadIds.length > 0)
    ) {
      setSelection(list[0]);
    }
  });

  useShallowEffect(() => {
    handleSync();
  }, [gamepadIds, selection]);

  return (
    <div className={twMerge('flex h-full flex-col', className)} {...props}>
      <ActionBox>
        <Heading
          subtitle='All of your connected controllers will appear here.'
          title='Controllers'
        />
      </ActionBox>

      <Tabs.Root defaultValue='gamepads'>
        <Tabs.List className='border-t-0 px-0'>
          <Tabs.Trigger value='gamepads'>Gamepads</Tabs.Trigger>
          <Tabs.Trigger value='controllers' {...STagNew(Sel.AccountSettings, 'ip_controllers_tab')}>
            IP Controllers
          </Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value='gamepads'>
          <div className='my-16 flex flex-col gap-32'>
            <AnimatePresence>
              {isEmpty(gamepadIds) ? (
                <motion.div key='_empty' {...Motions.Fade}>
                  <Callout className='w-fit' severity='warning' title='No Gamepads Detected'>
                    <Callout.P>
                      Make sure your gamepad is turned on as is connected to this computer. You may
                      need to push a button on the gamepad to wake it up.
                    </Callout.P>
                  </Callout>
                </motion.div>
              ) : (
                <motion.div className='flex gap-12' key='_gamepads' {...Motions.Fade}>
                  <ItemsList
                    copy={{
                      leftTitle: 'Saved Gamepads',
                      rightTitle: 'Status',
                      noItemsCopy: 'You currently have no saved controllers.'
                    }}
                    list={list}
                    selected={selection}
                    setSelected={setSelection}
                    search={{
                      copy: 'Search Again',
                      found: `${list.length} devices found`
                      // onSearch: () => alert('search')
                    }}
                  />

                  {!!selection && (
                    <div className='mr-12 flex flex-grow items-center rounded-b-8 bg-base-300 p-16'>
                      <Specimen gamepadId={selection.id} />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {!!selection && <Layout gamepadId={selection.id} />}
          </div>
        </Tabs.Content>
        <Tabs.Content value='controllers'>
          <ControllersList />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
};
